import { computed, ref } from 'vue'

/**
 * Announcement configuration
 * Update this content when you need to show a new announcement
 * Change the `id` to force the popup to show again for users who dismissed a previous one
 */
const ANNOUNCEMENT = {
  // Unique ID for this announcement - change this to show a new announcement
  id: 'weather-update-2026-01-24',

  // Title shown in the dialog header and dashboard card
  title: 'Parade Day Update',

  // Full content (supports HTML)
  content: `
    <p class="mb-4">
      We're moving forward rain or shine this evening. Due to weather conditions, the lineup time will be condensed to keep things moving safely and smoothly.
    </p>

    <p class="mb-4"><strong>Please note:</strong></p>

    <ul class="list-disc pl-6 mb-4 space-y-2">
      <li>Franklin Ave will remain open to vehicular traffic until 5:00 PM.</li>
      <li>Members in the parade should not begin lining up until after the street is closed to cars.</li>
      <li>Wristband distribution for members will take place indoors at St. Paul Lutheran Church (2624 Burgundy) from 4:00–6:00 PM.</li>
    </ul>

    <p class="mb-4 text-xl">
      Rain won't stop our rebellion. HOPE to see you on the route! 🌧️✨
    </p>
  `,

  // Set to false to disable the announcement entirely
  enabled: false,
}

const STORAGE_KEY = 'krewebacchus-announcement-dismissed'

// Module-level state so all usages share the same dialog visibility
const showDialog = ref(false)

/**
 * Composable for managing site-wide announcements
 * Shows a popup on login that can be dismissed, with state persisted in localStorage
 */
export function useAnnouncement() {
  // Check if current announcement has been dismissed
  const isDismissed = computed(() => {
    if (typeof window === 'undefined') return false
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY)
      return dismissed === ANNOUNCEMENT.id
    }
    catch {
      return false
    }
  })

  // Whether the announcement should be shown (enabled and not dismissed)
  const shouldShow = computed(() => {
    return ANNOUNCEMENT.enabled && !isDismissed.value
  })

  // Announcement data
  const announcement = computed(() => ({
    id: ANNOUNCEMENT.id,
    title: ANNOUNCEMENT.title,
    content: ANNOUNCEMENT.content,
    enabled: ANNOUNCEMENT.enabled,
  }))

  // Get first paragraph for preview (strip HTML tags for plain text preview)
  const preview = computed(() => {
    if (!ANNOUNCEMENT.enabled) return ''

    // Extract text content and get first ~200 chars
    const tempDiv = typeof document !== 'undefined' ? document.createElement('div') : null
    if (tempDiv) {
      tempDiv.innerHTML = ANNOUNCEMENT.content
      const text = tempDiv.textContent || tempDiv.innerText || ''
      // Get first meaningful chunk (up to 200 chars, end at word boundary)
      const trimmed = text.trim().substring(0, 250)
      const lastSpace = trimmed.lastIndexOf(' ')
      return lastSpace > 150 ? trimmed.substring(0, lastSpace) + '...' : trimmed + '...'
    }
    // Fallback: simple regex strip
    const text = ANNOUNCEMENT.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    const trimmed = text.substring(0, 250)
    const lastSpace = trimmed.lastIndexOf(' ')
    return lastSpace > 150 ? trimmed.substring(0, lastSpace) + '...' : trimmed + '...'
  })

  // Dismiss the current announcement
  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, ANNOUNCEMENT.id)
    }
    catch {
      // localStorage not available
    }
    showDialog.value = false
  }

  // Open the dialog (for "Read More" button)
  function openDialog() {
    showDialog.value = true
  }

  // Close dialog without dismissing (for X button)
  function closeDialog() {
    showDialog.value = false
  }

  // Trigger the popup if conditions are met (call after dues dialog closes)
  function triggerIfNeeded() {
    if (shouldShow.value) {
      showDialog.value = true
    }
  }

  return {
    announcement,
    preview,
    showDialog,
    shouldShow,
    isDismissed,
    dismiss,
    openDialog,
    closeDialog,
    triggerIfNeeded,
  }
}
